import type { AiVerificationSummary, AiVerificationTally } from '@citizen-walk/shared-types';

/**
 * 検証結果の wide-format サマリ表 + サマリ CSV を組み立てる pure helper 群
 * (Admin Wave AW4、docs/108 High #7)。
 *
 * 旧 `old2/apps/admin/src/lib/verification.ts` の
 * `getSummarySubColumns` / `getSubColumnValue` / `collectSummaryColumns` / `buildSummaryCsv` を
 * v2 に踏襲。client-side で完結する (admin が読める Post.verificationSummary のみを使うため)。
 * 詳細 CSV (参加者×質問 flatten) は server-side の exportVerificationDetailCsv callable のまま。
 */

/** 質問形式に応じた集計列のサブカラム種別 (CSV と表で共有)。 */
export type SummarySubColumnKey =
  | 'valid'
  | 'neutral'
  | 'invalid'
  | 'yes'
  | 'no'
  | 'scaleAvg'
  | 'scaleCount'
  | 'textCount'
  | 'choiceCounts';

export interface SummarySubColumn {
  key: SummarySubColumnKey;
  label: string;
}

export interface SummaryRowQuestion {
  scope: 'perPost' | 'perClassification';
  rank: string | null;
  questionId: string;
  questionLabel: string;
  questionType: string;
  tally: AiVerificationTally;
}

export interface SummaryRowPost {
  id: string;
  title: string;
  authorNickname: string;
  authorUid: string;
}

export interface SummaryQuestionColumn {
  scope: 'perPost' | 'perClassification';
  rank: string | null;
  questionId: string;
  questionLabel: string;
  questionType: string;
  /** ヘッダ表示用: scope/rank を含む短い接頭辞 */
  prefix: string;
  subColumns: SummarySubColumn[];
}

/** 質問型 → サブカラム定義。 */
export function getSummarySubColumns(questionType: string): SummarySubColumn[] {
  switch (questionType) {
    case 'three_state':
      return [
        { key: 'valid', label: '妥当' },
        { key: 'neutral', label: 'どちらでもない' },
        { key: 'invalid', label: '妥当ではない' },
      ];
    case 'binary':
      return [
        { key: 'valid', label: '妥当' },
        { key: 'invalid', label: '妥当ではない' },
      ];
    case 'yes_no':
      return [
        { key: 'yes', label: 'はい' },
        { key: 'no', label: 'いいえ' },
      ];
    case 'five_scale':
      return [
        { key: 'scaleAvg', label: '5段階平均' },
        { key: 'scaleCount', label: '5段階回答数' },
      ];
    case 'comment':
      return [{ key: 'textCount', label: '記述件数' }];
    case 'custom_choice':
      return [{ key: 'choiceCounts', label: '選択肢分布(JSON)' }];
    default:
      return [
        { key: 'valid', label: '値1' },
        { key: 'neutral', label: '値2' },
        { key: 'invalid', label: '値3' },
      ];
  }
}

/** Tally から指定サブカラムの値を取り出して文字列化。 */
export function getSubColumnValue(tally: AiVerificationTally, key: SummarySubColumnKey): string {
  switch (key) {
    case 'valid':
      return tally.valid !== undefined ? String(tally.valid) : '';
    case 'neutral':
      return tally.neutral !== undefined ? String(tally.neutral) : '';
    case 'invalid':
      return tally.invalid !== undefined ? String(tally.invalid) : '';
    case 'yes':
      return tally.yes !== undefined ? String(tally.yes) : '';
    case 'no':
      return tally.no !== undefined ? String(tally.no) : '';
    case 'scaleAvg':
      if (tally.scaleSum !== undefined && tally.scaleCount && tally.scaleCount > 0) {
        return (tally.scaleSum / tally.scaleCount).toFixed(2);
      }
      return '';
    case 'scaleCount':
      return tally.scaleCount !== undefined ? String(tally.scaleCount) : '';
    case 'textCount':
      return tally.textCount !== undefined ? String(tally.textCount) : '';
    case 'choiceCounts':
      return tally.choiceCounts && Object.keys(tally.choiceCounts).length > 0
        ? JSON.stringify(tally.choiceCounts)
        : '';
  }
}

/**
 * 全行の質問の和集合をベースに wide format の列構成を構築する。
 * 列順は perPost が先、その後 perClassification (rank 昇順、同 rank 内は questionId 昇順)。
 */
export function collectSummaryColumns(
  rows: Array<{ questions: SummaryRowQuestion[] }>,
): SummaryQuestionColumn[] {
  const seen = new Map<string, SummaryQuestionColumn>();
  for (const row of rows) {
    for (const q of row.questions) {
      const key = `${q.scope}|${q.rank ?? ''}|${q.questionId}`;
      if (seen.has(key)) continue;
      const prefix = q.scope === 'perPost' ? '投稿全体' : `分類#${q.rank ?? ''}`;
      seen.set(key, {
        scope: q.scope,
        rank: q.rank,
        questionId: q.questionId,
        questionLabel: q.questionLabel,
        questionType: q.questionType,
        prefix,
        subColumns: getSummarySubColumns(q.questionType),
      });
    }
  }
  return [...seen.values()].sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === 'perPost' ? -1 : 1;
    if (a.scope === 'perClassification') {
      const ra = Number(a.rank ?? 0);
      const rb = Number(b.rank ?? 0);
      if (ra !== rb) return ra - rb;
    }
    return a.questionId.localeCompare(b.questionId);
  });
}

/**
 * AiVerificationSummary (perPost / perClassification tally) + 質問メタ (label/type 解決) から
 * SummaryRowQuestion[] を組み立てる。テンプレ未登録 (過去テンプレ切替で残った) 質問も拾う。
 */
export function buildSummaryRowQuestions(
  summary: AiVerificationSummary,
  resolve: (scope: 'perPost' | 'perClassification', qid: string) => { label: string; type: string },
): SummaryRowQuestion[] {
  const rows: SummaryRowQuestion[] = [];
  for (const [qid, tally] of Object.entries(summary.perPost ?? {})) {
    const meta = resolve('perPost', qid);
    rows.push({
      scope: 'perPost',
      rank: null,
      questionId: qid,
      questionLabel: meta.label,
      questionType: meta.type,
      tally,
    });
  }
  const ranks = Object.keys(summary.perClassification ?? {}).sort((a, b) => Number(a) - Number(b));
  for (const rank of ranks) {
    const inner = summary.perClassification?.[rank] ?? {};
    for (const [qid, tally] of Object.entries(inner)) {
      const meta = resolve('perClassification', qid);
      rows.push({
        scope: 'perClassification',
        rank,
        questionId: qid,
        questionLabel: meta.label,
        questionType: meta.type,
        tally,
      });
    }
  }
  return rows;
}

/** RFC 4180 準拠で 1 セルをエスケープ。 */
function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells: unknown[]): string {
  return cells.map(csvCell).join(',');
}

/**
 * サマリ CSV (1 行 = 1 投稿、質問ごとに列を展開した wide format)。old2 buildSummaryCsv 踏襲。
 */
export function buildSummaryCsv(
  rows: Array<{
    post: SummaryRowPost;
    responseCount: number;
    /** 自分の投稿への回答数 (完了数)。分離集計前の summary は null / undefined → 空欄。 */
    ownResponseCount?: number | null;
    /** 他の人の投稿への回答数 (完了数)。同上。 */
    othersResponseCount?: number | null;
    questions: SummaryRowQuestion[];
  }>,
): string {
  const columns = collectSummaryColumns(rows);

  const fixedHeader = [
    '投稿ID',
    'タイトル',
    '投稿者',
    '投稿者UID',
    '回答数',
    '自分の投稿への回答数',
    '他の人の投稿への回答数',
  ];
  const dynamicHeader: string[] = [];
  for (const col of columns) {
    for (const sub of col.subColumns) {
      const label = col.questionLabel || col.questionId;
      dynamicHeader.push(`${col.prefix} ${label} - ${sub.label}`);
    }
  }
  const header = csvRow([...fixedHeader, ...dynamicHeader]);

  const lines = rows.map((r) => {
    const tallyMap = new Map<string, AiVerificationTally>();
    for (const q of r.questions) {
      tallyMap.set(`${q.scope}|${q.rank ?? ''}|${q.questionId}`, q.tally);
    }
    const cells: unknown[] = [
      r.post.id,
      r.post.title,
      r.post.authorNickname,
      r.post.authorUid,
      r.responseCount,
      r.ownResponseCount ?? '',
      r.othersResponseCount ?? '',
    ];
    for (const col of columns) {
      const tally = tallyMap.get(`${col.scope}|${col.rank ?? ''}|${col.questionId}`);
      for (const sub of col.subColumns) {
        cells.push(tally ? getSubColumnValue(tally, sub.key) : '');
      }
    }
    return csvRow(cells);
  });

  return [header, ...lines].join('\r\n');
}
